import {yupResolver} from '@hookform/resolvers/yup'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Dialog from '@material-ui/core/Dialog'
import Divider from '@material-ui/core/Divider'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormGroup from '@material-ui/core/FormGroup'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import {fetchSalesDetailSearchApi} from 'api/salesApi'
import {useBoldInputStyle} from 'components/common/useBoldInputStyle'
import SelectBoldCountry from 'components/Dialog/BoldSelect/SelectBoldCountry'
import SelectBoldIndustryWrapper from 'components/Dialog/BoldSelect/SelectBoldIndustry'
import SelectBoldSalesName from 'components/Dialog/BoldSelect/SelectBoldSalesName'
import {nullCheck} from 'components/Dialog/User/Read'
import {useAppDispatch, useAppSelector} from 'hooks'
import useStyles from 'components/Dialog/User/Style'
import {
  A_CHARGE_PROPERTY_TYPE,
  COMPANY_PROPERTY_TYPE,
  DATA_SHARE_TYPE,
  MOU_PROPERTY_TYPE,
  NO_CHARGE_PROPERTY_TYPE,
  NON_PROFIT_ORGANIZATION_TYPE,
  NONE_PROPERTY_TYPE,
} from 'constants/DialogConstant'
import {REGEX_PHONE} from 'constants/CommonConstant'
import useAuth from 'features/auth/useAuth'
import {
  closeSalesOrgEditDialog,
  selectSalesOrgEditDialogOpen,
} from 'features/modal/modalSlice'
import {updateSalesOrganizationAction} from 'features/sales/salesSlice'
import React, {useEffect} from 'react'
import {Controller, SubmitHandler, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useQuery, useQueryClient} from 'react-query'
import * as yup from 'yup'
import {object, TypeOf} from 'yup'
import useFailureModal from 'features/modal/useFailureModal'
import useSuccessModal from 'features/modal/useSuccessModal'
import useSalesAllOrgFetch from 'features/sales/useSalesAllOrg'
import SelectBoldCountryCode from 'components/Dialog/BoldSelect/SelectBoldCountryCode'
import useNoticeConfirm from 'features/modal/useNoticeConfirm'

// 서버로 보낼때 사용하는 type
type OrgInfoEditInputType = {
  uid: string
  email?: string | null
  orgName?: string | null
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  countryCode?: string | null
  userType?: string | null
  profile?: File | null
  tel?: string | null
  area?: number | null
  address?: string | null
  businessNo?: string | null
  institutionNo?: string | null
  doctorCount?: number | null
  joinRoute?: string | null
  interestArea?: string | null
  password?: string | null
  passwordConfirm?: string | null
  salesName?: string | null
  authorityGroup?: string | null
  property?: Array<string> | null
  startDate?: string | null
  endDate?: string | null
}

interface SalesOrganizationInfo {
  oid?: string
  open?: boolean
}

export default function SalesOrganizationEditDialog({
  oid,
}: SalesOrganizationInfo) {
  const {onOpen: onNoticeDialogOpen} = useNoticeConfirm()
  const {t} = useTranslation()
  const classes = useStyles()
  const inputClasses = useBoldInputStyle()
  const dispatch = useAppDispatch()
  const dialogOpen = useAppSelector(selectSalesOrgEditDialogOpen)
  const queryClient = useQueryClient()

  const {user: currentUser} = useAuth()
  const fetchInfoParam = {
    uid: currentUser?.uid ?? '',
    oid,
  }

  const {isSuccess, data: orgInfoData} = useQuery(
    ['fetchSalesOrgInfo', fetchInfoParam],
    () => fetchSalesDetailSearchApi(fetchInfoParam),
    {
      onSuccess: () => {},
      onError: () => {},
    },
  )

  interface EditForm extends TypeOf<typeof AddSchema> {}
  // 유효성 체크
  const AddSchema = object({
    orgName: yup.string().min(1, t('IRegisterHospitalName')),
    firstName: yup.string().min(1, t('INameRequired')),
    lastName: yup.string().min(1, t('INameRequired')),
    area: yup.string().min(1),
    email: yup.string(),
    address: yup.string().min(1),
    businessNo: yup.string().min(1),
    countryCode: yup.string(),
    tel: yup.string().matches(REGEX_PHONE, t('ITelInvalid')),
    institutionNo: yup.string(),
    interestArea: yup.string(),
    doctorCount: yup.string(),
    joinRoute: yup.string(),
    salesName: yup.string(),
    authorityGroup: yup.string(),
  })
  const {
    control,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm<EditForm>({
    resolver: yupResolver(AddSchema),
  })

  // 전화번호 - 제거
  const replaceValue = orgInfoData?.data?.orgDetail?.tel ?? ''
  const telValue = replaceValue.replace(/-/gi, '')

  const [propertyState, setPropertyState] = React.useState({
    MOU: false,
    COMPANY: false,
    A_CHARGE: false,
    NO_CHARGE: false,
    DATA_SHARE: false,
    NON_PROFIT_ORGANIZATION: false,
  })
  const [industry, setIndustry] = React.useState<Industry>({
    id: 0,
    parent: 0,
    depth: 0,
    title: '',
  })
  const [detailIndustry, setDetailIndustry] = React.useState<Industry>({
    id: 0,
    parent: 0,
    depth: 0,
    title: '',
  })

  const [divisionId, setDivisionId] = React.useState<Industry>({
    id: 0,
    parent: 0,
    depth: 0,
    title: '',
  })
  const [positionId, setPositionId] = React.useState<Industry>({
    id: 0,
    parent: 0,
    depth: 0,
    title: '',
  })
  const handleDivisionChanged = (industry: Industry) => setDivisionId(industry)
  const handlePositionChanged = (industry: Industry) => setPositionId(industry)

  const propertyValueList: string[] = []
  const sendPropertyValue = () => {
    if (propertyState.MOU) propertyValueList.push(MOU_PROPERTY_TYPE)
    if (propertyState.COMPANY) propertyValueList.push(COMPANY_PROPERTY_TYPE)
    if (propertyState.A_CHARGE) propertyValueList.push(A_CHARGE_PROPERTY_TYPE)
    if (propertyState.NO_CHARGE) propertyValueList.push(NO_CHARGE_PROPERTY_TYPE)
    if (propertyState.DATA_SHARE) propertyValueList.push(DATA_SHARE_TYPE)
    if (propertyState.NON_PROFIT_ORGANIZATION)
      propertyValueList.push(NON_PROFIT_ORGANIZATION_TYPE)
    if (
      !propertyState.MOU &&
      !propertyState.COMPANY &&
      !propertyState.A_CHARGE &&
      !propertyState.NO_CHARGE
    ) {
      propertyValueList.push(NONE_PROPERTY_TYPE)
    }
    return true
  }
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {onOpen: onSuccessModalOpen} = useSuccessModal()

  const handlePropertyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPropertyState({
      ...propertyState,
      [event.target.value as string]: event.target.checked,
    })
  }

  const handleDialogClose = () => {
    dispatch(closeSalesOrgEditDialog())
    reset()
  }

  const query = {
    uid: currentUser?.uid,
    paging: {
      page: 0,
      size: 5,
    },
    search: null,
    period: {
      startDate: '',
      endDate: '',
    },
  }
  const {refetch} = useSalesAllOrgFetch(query)

  const onSubmit = async (data: OrgInfoEditInputType) => {
    data.email = data?.email ?? ''
    if (sendPropertyValue()) data.property = propertyValueList
    data.uid = orgInfoData?.data.userInfoList[0].uid ?? data.uid

    // submit object에서 undefined인 값 삭제
    const submitOrgInfo = Object.fromEntries(
      // eslint-disable-next-line
      Object.entries(data).filter(([_, v]) => v != null),
    )
    const updateAction = await dispatch(
      updateSalesOrganizationAction(submitOrgInfo),
    )
    const updateResult = updateAction.payload as CommonResponse

    if (updateResult.success) {
      onSuccessModalOpen(t('IProcessSuccess'))
      queryClient.invalidateQueries('fetchSalesOrgInfo')
      refetch()
      handleDialogClose()
    }
    if (!updateResult.success) {
      onFailureModalOpen(updateResult.message)
      handleDialogClose()
    }
    reset()
  }

  const onSubmitHandler: SubmitHandler<EditForm> = (data) =>
    onSubmit(data as OrgInfoEditInputType)

  useEffect(() => {
    const orgPropertyArray = orgInfoData?.data?.orgDetail?.property
    if (orgPropertyArray) {
      setPropertyState({
        ...propertyState,
        [NO_CHARGE_PROPERTY_TYPE]: orgPropertyArray.includes(
          NO_CHARGE_PROPERTY_TYPE,
        ),
        [MOU_PROPERTY_TYPE]: orgPropertyArray.includes(MOU_PROPERTY_TYPE),
        [A_CHARGE_PROPERTY_TYPE]: orgPropertyArray.includes(
          A_CHARGE_PROPERTY_TYPE,
        ),
        [COMPANY_PROPERTY_TYPE]: orgPropertyArray.includes(
          COMPANY_PROPERTY_TYPE,
        ),
        [DATA_SHARE_TYPE]: orgPropertyArray.includes(DATA_SHARE_TYPE),
        [NON_PROFIT_ORGANIZATION_TYPE]: orgPropertyArray.includes(
          NON_PROFIT_ORGANIZATION_TYPE,
        ),
      })
    }
  }, [orgInfoData])

  // 병원정보 권한 셋팅
  useEffect(() => {
    if (orgInfoData?.data?.orgDetail?.industry?.id) {
      setIndustry(orgInfoData.data.orgDetail.industry)
    }
    if (orgInfoData?.data?.orgDetail?.detailIndustry?.id) {
      setDetailIndustry(orgInfoData.data.orgDetail.detailIndustry)
    }
  }, [orgInfoData])

  useEffect(() => {}, [divisionId, positionId])

  return (
    <div>
      <Dialog open={dialogOpen} fullWidth maxWidth='md'>
        <form onSubmit={handleSubmit(onSubmitHandler)}>
          <div className={classes.root}>
            {/* 상단 타이틀 컨테이너  */}
            <div className={classes.longTitleContainer}>
              <div className={classes.containerTitle}>
                <div className={classes.closeButtonWrap}>
                  <IconButton
                    color='secondary'
                    aria-label='favorite'
                    className={classes.closeButton}
                    onClick={() => {
                      onNoticeDialogOpen({
                        title: '',
                        message: t('ICancleConfirm'),
                      }).then((result) => {
                        if (result.payload) {
                          handleDialogClose()
                        }
                      })
                    }}
                  >
                    <CloseIcon className={classes.closeIcon} />
                  </IconButton>
                </div>
                <Typography className={classes.title}>
                  {t('IEditInstituteInformation')}
                </Typography>
              </div>
            </div>
            <Divider className={classes.titleDivider} />
            {isSuccess && (
              <div className={classes.scroll}>
                <Typography className={classes.padding} />
                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>
                      {' '}
                      {t('IInstituteTitleName')}{' '}
                    </p>
                    <p className={inputClasses.require}>*</p>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Controller
                      name='orgName'
                      control={control}
                      defaultValue={orgInfoData?.data?.orgDetail?.orgName ?? ''}
                      render={({field}) => {
                        return (
                          <input
                            className={inputClasses.input}
                            // id='orgName'
                            // label={t('IOrgName')}
                            // variant='outlined'
                            // required
                            // defaultValue={orgDetailInfo?.orgName ?? ''}
                            // error={!!errors.orgName}
                            {...field}
                          />
                        )
                      }}
                    />
                  </div>
                </div>
                <span className={classes.errorText}>
                  {errors.orgName && (
                    <div>{t(errors.orgName?.message ?? '')}</div>
                  )}
                </span>
                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}> {t('ICeoName')} </p>
                    <p className={inputClasses.require}>*</p>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Controller
                      name='firstName'
                      control={control}
                      rules={{required: false}}
                      render={({field}) => (
                        <input
                          // id='org_name'
                          // label={t('IFirstName')}
                          // variant='outlined'
                          // required
                          className={inputClasses.input}
                          defaultValue={
                            orgInfoData?.data?.orgDetail?.firstName ?? '-'
                          }
                          // error={!!errors.firstName}
                          {...field}
                        />
                      )}
                    />
                    <Typography className={classes.padding} />
                    <Controller
                      name='lastName'
                      control={control}
                      rules={{required: false}}
                      render={({field}) => (
                        <input
                          className={inputClasses.input}
                          // id='org_name'
                          // label={t('ILastName')}
                          // variant='outlined'
                          // required
                          defaultValue={
                            orgInfoData?.data?.orgDetail?.lastName ?? '-'
                          }
                          // error={!!errors.lastName}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <span className={classes.errorText}>
                    {errors.firstName && !errors.lastName && (
                      <div>{t(errors.firstName?.message ?? '')}</div>
                    )}
                    {errors.lastName && !errors.firstName && (
                      <div>{t(errors.lastName?.message ?? '')}</div>
                    )}
                    {errors.firstName && errors.lastName && (
                      <div>{t(errors.lastName?.message ?? '')}</div>
                    )}
                  </span>
                </div>
                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}> {t('IEmail')}</p>
                    <p className={inputClasses.require}>*</p>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Controller
                      name='email'
                      control={control}
                      rules={{required: false}}
                      render={({field}) => (
                        <input
                          className={inputClasses.input}
                          // id='org_name'
                          // label={t('ILastName')}
                          // variant='outlined'
                          // required
                          defaultValue={
                            orgInfoData?.data?.orgDetail?.email ?? '-'
                          }
                          // error={!!errors.lastName}
                          disabled
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>{t('ICountry')}</p>
                    <p className={inputClasses.require}>*</p>
                  </div>

                  {nullCheck(orgInfoData?.data?.orgDetail?.country) && (
                    <Controller
                      name='countryCode'
                      control={control}
                      // defaultValue={String(
                      //   orgInfoData?.data?.orgDetail?.country.number ?? '',
                      // )}
                      render={({field}) => (
                        <SelectBoldCountryCode
                          defaultCountry={orgInfoData?.data?.orgDetail?.country}
                          error={!!errors.countryCode}
                          {...field}
                        />
                      )}
                    />
                  )}
                  {!nullCheck(orgInfoData?.data?.orgDetail?.country) && (
                    <Controller
                      name='countryCode'
                      control={control}
                      defaultValue='KR'
                      render={({field}) => (
                        <SelectBoldCountry
                          error={!!errors.countryCode}
                          {...field}
                        />
                      )}
                    />
                  )}
                </div>

                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>{t('ITel')}</p>
                    <p className={inputClasses.require}>*</p>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Controller
                      name='tel'
                      control={control}
                      rules={{required: false}}
                      render={({field}) => (
                        <input
                          // label={t('ITel')}
                          // variant='outlined'
                          // required
                          className={inputClasses.input}
                          defaultValue={telValue}
                          // error={!!errors.tel}
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>
                      {t('IIndustryDetail')}
                    </p>
                    <p className={inputClasses.require}>*</p>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <SelectBoldIndustryWrapper
                      parent={industry.id}
                      depth={3}
                      selectedValue={t('ISelectRequired')}
                      onChangeData={handleDivisionChanged}
                    />
                    <Typography className={classes.padding} />
                    <SelectBoldIndustryWrapper
                      parent={detailIndustry.id}
                      depth={4}
                      selectedValue={t('ISelectRequired')}
                      onChangeData={handlePositionChanged}
                    />
                  </div>
                </div>

                {/* <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>{t('IArea')}</p>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Controller
                      name='area'
                      control={control}
                      rules={{required: false}}
                      render={({field}) => (
                        <input
                          className={inputClasses.input}
                          defaultValue={
                            orgInfoData?.data?.orgDetail?.area ?? ''
                          }
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div> */}

                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>{t('IAddress')}</p>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Controller
                      name='address'
                      control={control}
                      rules={{required: false}}
                      render={({field}) => (
                        <input
                          className={inputClasses.input}
                          defaultValue={
                            orgInfoData?.data?.orgDetail?.address ?? ''
                          }
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>

                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>
                      {t('IBusinessNo')}
                    </p>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Controller
                      name='businessNo'
                      control={control}
                      rules={{required: false}}
                      defaultValue={
                        orgInfoData?.data?.orgDetail?.businessNo ?? ''
                      }
                      render={({field}) => (
                        <input className={inputClasses.input} {...field} />
                      )}
                    />
                  </div>
                </div>

                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>
                      {t('IOrgNursingInstitutionNumber')}
                    </p>
                  </div>
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Controller
                      name='institutionNo'
                      control={control}
                      rules={{required: false}}
                      defaultValue={
                        orgInfoData?.data?.orgDetail?.institutionNo ?? ''
                      }
                      render={({field}) => (
                        <input className={inputClasses.input} {...field} />
                      )}
                    />
                  </div>
                </div>

                {/* <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>
                      {t('IOrgUserCount')}
                    </p>
                  </div>
                  <Controller
                    name='doctorCount'
                    control={control}
                    rules={{required: false}}
                    defaultValue={String(
                      orgInfoData?.data?.orgDetail?.doctorCount ?? '',
                    )}
                    render={({field}) => (
                      <input className={inputClasses.input} {...field} />
                    )}
                  />
                </div> */}

                {/* <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>{t('IJoinRoute')}</p>
                  </div>

                  <Controller
                    name='joinRoute'
                    control={control}
                    defaultValue={orgInfoData?.data?.orgDetail?.joinRoute ?? ''}
                    render={({field}) => (
                      <SelectBoldJoinRoute
                        // defaultValue={orgDetailInfo?.joinRoute ?? ''}
                        {...field}
                      />
                    )}
                  />
                </div> */}

                {/* <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>
                      {t('IInterestArea')}
                    </p>
                  </div>

                  <Controller
                    name='interestArea'
                    control={control}
                    defaultValue={
                      orgInfoData?.data?.orgDetail?.interestArea ?? 'ADHD'
                    }
                    render={({field}) => <SelectBoldInterestArea {...field} />}
                  />
                </div> */}
                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>
                      {t('ISalesManagerEdit')}
                    </p>
                  </div>

                  <Controller
                    name='salesName'
                    control={control}
                    defaultValue={orgInfoData?.data?.orgDetail?.salesName ?? ''}
                    render={({field}) => <SelectBoldSalesName {...field} />}
                  />
                </div>

                <div className={classes.inputContainer}>
                  <div className={inputClasses.labelWrapContainer}>
                    <p className={inputClasses.inputLabel}>{t('IProperty')}</p>
                  </div>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={propertyState.MOU}
                          onChange={handlePropertyChange}
                          name='MOU'
                          value={MOU_PROPERTY_TYPE}
                        />
                      }
                      label='Mou'
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={propertyState.COMPANY}
                          onChange={handlePropertyChange}
                          name='아이메디신'
                          value={COMPANY_PROPERTY_TYPE}
                        />
                      }
                      label={t('IAppCompany')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={propertyState.A_CHARGE}
                          onChange={handlePropertyChange}
                          name='유료'
                          value={A_CHARGE_PROPERTY_TYPE}
                        />
                      }
                      label={t('IFree')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={propertyState.NO_CHARGE}
                          onChange={handlePropertyChange}
                          name='무료'
                          value={NO_CHARGE_PROPERTY_TYPE}
                        />
                      }
                      label={t('IPaid')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={propertyState.DATA_SHARE}
                          onChange={handlePropertyChange}
                          name='데이터공유'
                          value={DATA_SHARE_TYPE}
                        />
                      }
                      label={t('IDataShare')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={propertyState.NON_PROFIT_ORGANIZATION}
                          onChange={handlePropertyChange}
                          name='비영리기관'
                          value={NON_PROFIT_ORGANIZATION_TYPE}
                        />
                      }
                      label={t('INonProfitOrganization')}
                    />
                  </FormGroup>
                </div>
              </div>
            )}
            <Typography className={classes.padding} />
            <Typography className={classes.padding} />
            {/* 하단 버튼 컨테이너  */}
            <div className={classes.buttonContainerBgColor}>
              <div className={classes.buttonContainer}>
                <Button
                  variant='contained'
                  color='default'
                  disableElevation
                  onClick={() => {
                    onNoticeDialogOpen({
                      title: '',
                      message: t('ICancleConfirm'),
                    }).then((result) => {
                      if (result.payload) {
                        handleDialogClose()
                      }
                    })
                  }}
                  className={classes.okButton}
                >
                  {t('ICancel')}
                </Button>
                <Typography className={classes.smallPadding} />
                <Button
                  variant='contained'
                  color='primary'
                  disableElevation
                  className={classes.okButtonColor}
                  type='submit'
                >
                  {t('IOk')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Dialog>
    </div>
  )
}
